import { Log } from '@/parsing/Log';
import { LogEntry, LogEntryType } from '@/parsing/LogEntry';

import {
  MapEventAnimationTimelineMarker,
  MapEventInfo,
  MapEventTimelineMarker,
  TimelineRow,
  TimelineRowGroup,
} from '../../TimelineData';
import { obtainFreeRowForMarker } from '../obtainFreeRowForMarker';
import { TimelineGeneratorContext } from '../TimelineGeneratorContext';

export function generateIosMapEventsGroupFromLog(
  log: Log,
  ctx: TimelineGeneratorContext,
): TimelineRowGroup {
  const rowsForSeparateEvents: TimelineRow[] = [];
  const rowsForWholeAnimations: TimelineRow[] = [];

  let startLogEntryData:
    | {
        infoJson: any;
        startLogEntry: LogEntry;
        endLogEntry: LogEntry;
        mapEventInfoList: MapEventInfo[];
      }
    | undefined;

  for (const entry of log.entries) {
    if (entry.entryType !== LogEntryType.Generic) {
      continue;
    }
    if (entry.extra.generic?.module !== 'APPLICATIONEVENT') {
      continue;
    }
    if (entry.content.includes('UIKitApplicationEventsLogger is set up')) {
      continue;
    }

    try {
      const info = JSON.parse(entry.content);
      const id: string | undefined = info.identifier;
      if (!id) {
        continue;
      }

      if (!id.startsWith('map')) {
        continue;
      }

      const tooltipContent = entry.content;
      let textOnMarker = '';
      let fillColor = '';
      let textOnMarkerColor = '';

      const mapEventInfo: MapEventInfo = {
        logEntryIndex: entry.index,
        mapContext: info.eventCustomInfo.activeMapContext,
        timeMs: entry.date.getTime(),
        center: {
          lat: info.eventCustomInfo.center[0],
          lng: info.eventCustomInfo.center[1],
        },
        frame: {
          x: info.eventCustomInfo.frame[0][0],
          y: info.eventCustomInfo.frame[0][1],
          w: info.eventCustomInfo.frame[1][0],
          h: info.eventCustomInfo.frame[1][1],
        },
        padding: {
          top: info.eventCustomInfo.padding[0],
          right: info.eventCustomInfo.padding[1],
          bottom: info.eventCustomInfo.padding[2],
          left: info.eventCustomInfo.padding[3],
        },
        zoom: info.eventCustomInfo.zoom,
        viewportBounds: {
          ne: {
            lat: info.eventCustomInfo.viewportBounds[0],
            lng: info.eventCustomInfo.viewportBounds[1],
          },
          sw: {
            lat: info.eventCustomInfo.viewportBounds[2],
            lng: info.eventCustomInfo.viewportBounds[3],
          },
        },
      };

      if (id === 'mapViewportWillMove') {
        textOnMarker = `Start`;
        fillColor = '#ffaf4a';
        textOnMarkerColor = '#7c5011';

        startLogEntryData = {
          infoJson: info,
          startLogEntry: entry,
          endLogEntry: entry,
          mapEventInfoList: [mapEventInfo],
        };

        if (info.isFromGestureRecognizer === true) {
          fillColor = '#00ff49';
        }
      } else if (id === 'mapViewportUpdate') {
        textOnMarker = ``;
        fillColor = '#f7d0ad';
        textOnMarkerColor = '#726154';

        if (typeof startLogEntryData === 'undefined') {
          startLogEntryData = {
            infoJson: info,
            startLogEntry: entry,
            endLogEntry: entry,
            mapEventInfoList: [mapEventInfo],
          };

          textOnMarker = `Start`;
          fillColor = '#de70a6';
          textOnMarkerColor = '#7a0050';
        } else {
          startLogEntryData.mapEventInfoList.push(mapEventInfo);
          startLogEntryData.endLogEntry = entry;
        }

        if (startLogEntryData.infoJson.isFromGestureRecognizer === true) {
          fillColor = '#93fa9f';
        }
      } else if (id === 'mapViewportIdle') {
        textOnMarker = `End`;
        fillColor = '#b2e9f8';
        textOnMarkerColor = '#4f7476';

        if (startLogEntryData) {
          startLogEntryData.mapEventInfoList.push(mapEventInfo);
          startLogEntryData.endLogEntry = entry;

          if (startLogEntryData.infoJson.isFromGestureRecognizer === true) {
            fillColor = '#007400';
          }

          // TODO: implement animation
          // createMapEventAnimationMarker({
          //   rows: rowsForWholeAnimations,
          //   ctx,
          //   startLogEntry: startLogEntryData.startLogEntry,
          //   endLogEntry: startLogEntryData.endLogEntry,
          //   mapEventInfoList: startLogEntryData.mapEventInfoList,
          // });
        }

        startLogEntryData = undefined;
      } else {
        continue;
      }

      const marker: MapEventTimelineMarker = {
        markerId: ctx.nextMarkerId,
        logEntry: entry,
        startTimeMs: entry.date.getTime(),
        endTimeMs: undefined,
        fillColor,
        textOnMarker,
        textOnMarkerColor,
        tooltipContent,
        ...mapEventInfo,
      };
      ctx.nextMarkerId += 1;

      const row = obtainFreeRowForMarker(rowsForSeparateEvents, marker);
      row.markers.push(marker);
    } catch (error) {
      console.error('Cannot parse JSON for APPLICATIONEVENT for map', entry.content, error);
      continue;
    }
  }

  const rowGroup: TimelineRowGroup = {
    name: 'Map updates',
    rows: rowsForWholeAnimations.concat(rowsForSeparateEvents),
    expanded: true,
  };
  return rowGroup;
}

function createMapEventAnimationMarker(args: {
  rows: TimelineRow[];
  startLogEntry: LogEntry;
  endLogEntry: LogEntry;
  mapEventInfoList: MapEventInfo[];
  ctx: TimelineGeneratorContext;
}) {
  const marker: MapEventAnimationTimelineMarker = {
    markerId: args.ctx.nextMarkerId,
    logEntry: args.startLogEntry,
    alternativeLogEntry: args.endLogEntry,
    startTimeMs: args.mapEventInfoList[0].timeMs,
    endTimeMs: args.mapEventInfoList[args.mapEventInfoList.length - 1].timeMs,
    fillColor: '#ccabff',
    textOnMarker: undefined,
    textOnMarkerColor: undefined,
    tooltipContent: 'Map animation',
    mapEvents: args.mapEventInfoList,
  };
  args.ctx.nextMarkerId += 1;

  const row = obtainFreeRowForMarker(args.rows, marker);
  row.markers.push(marker);
}
